// All user-visible copy for the admin console, in one place and marked DRAFT so it can be
// voice-reviewed later (same rule as the mobile app's src/content/copy.ts). Plain, short,
// warm sentences. No em dashes or en dashes anywhere in these strings.
export const COPY = {
  draft: true,
  appName: 'Hydrus Owners Club admin',
  login: {
    title: 'Hydrus admin',
    blurb: 'Enter the admin access code to sign in. This is a local gate for now; Cloudflare Access replaces it at cutover.',
    apiLabel: 'Engine API URL',
    tokenLabel: 'Admin access code',
    button: 'Sign in',
    bad: 'That code did not work. Check the URL and try again.',
  },
  nav: {
    overview: 'Overview',
    applications: 'Applications',
    members: 'Members',
    money: 'Money',
    payouts: 'Payouts',
    moderation: 'Crew Feed',
    reports: 'Reports',
    game: 'Game',
  },
  overview: {
    title: 'Overview',
    sub: 'A quick read on the club today.',
    needsYou: 'Needs you',
    allClear: 'Nothing needs you right now. Nice.',
    applicationsToReview: 'applications to review',
    flaggedPosts: 'flagged posts in the feed',
    proofToConfirm: 'proofs to confirm',
    couponToReview: 'coupon lines to review',
    nextBatch: 'Next payout batch',
  },
  applications: {
    title: 'Applications',
    sub: 'A board is the qualifying purchase. Auto-approved orders land in their own tab; everyone else waits here for review.',
    empty: 'No applications in this view.',
    approve: 'Approve',
    deny: 'Decline',
    bulkApprove: 'Approve selected',
    ownershipFound: 'Hydrus purchase found',
    ownershipFlag: 'No Hydrus purchase found',
    proof: 'Proof on file',
    tabNeedsReview: 'Needs review',
    tabAutoApproved: 'Auto-approved',
    tabDecided: 'Decided',
    verified: 'Ownership verified',
    unverified: 'Not verified yet',
    confirmProof: 'Confirm proof',
    autoBadge: 'Auto-approved',
    undo: 'Undo (7 days)',
    undoConfirm: 'Undo this auto-approval? It closes the member and disables their code.',
    fraudTitle: 'Signals to check',
    needsProof: 'Confirm a receipt, serial or photo before you can approve.',
    boardFound: 'Board order found',
    proofToConfirm: 'Proof to confirm',
    noBoard: 'No board found',
    askForProof: 'Ask for proof',
    viewProof: 'View proof',
    proofViewerTitle: 'Proof on file',
    declineTitle: 'Decline this application?',
    declineBlurb: 'The applicant sees a kind message and can apply again. Add a note if you want to say why.',
    declinePreview: 'They will see:',
    declinePreviewText: 'Not this time. You can apply again any time; owning a Hydrus board is all it takes.',
    undoToast: 'Done. ',
    undoAction: 'Undo',
    askForProofToast: 'Asked for proof. The applicant can add a receipt, serial or photo.',
    proofConfirmedToast: 'Proof confirmed for ',
    noEmail: 'No email on file',
  },
  members: {
    title: 'Members',
    sub: 'Search by name, code or Crew number. Filter by program and standing.',
    search: 'Search name, code or Crew number',
    empty: 'No members match.',
    addByHand: 'Add member',
    noSharing: 'Owner, no sharing tools',
    activeFilter: 'Active in 30 days',
  },
  money: {
    title: 'Money',
    sub: 'Commission owed, held, approved and paid. Dollars are internal and never shown to members.',
    liability: 'Owed to members (not paid yet)',
    held: 'Held',
    approved: 'Approved',
    paid: 'Paid',
    couponTitle: 'Coupon site review queue',
    couponSub: 'Deal site and multi code orders wait here. Approve the ones that are real, decline the rest. Nothing is clawed back after payout.',
    couponEmpty: 'Nothing to review right now.',
    adjTitle: 'Adjustments log',
    adjEmpty: 'No adjustments yet.',
  },
  payouts: {
    title: 'Payouts',
    sub: 'The monthly batch preview. Review who gets paid and how much.',
    approve: 'Approve batch',
    disabled: 'Payout execution is off until cutover. You can review the batch, but nothing sends yet.',
    empty: 'No one is ready for payout this cycle.',
    belowMin: 'Below the minimum this cycle',
    storeCredit: 'Store credit',
    cash: 'Cash',
    minFromSettings: 'Minimum is set in settings (min_payout).',
    confirmTitle: 'Approve this batch?',
  },
  moderation: {
    title: 'Crew Feed',
    sub: 'Reported and held posts. Hide, remove, restore, or ban a member.',
    empty: 'Nothing needs attention right now.',
    hide: 'Hide',
    remove: 'Remove',
    restore: 'Restore',
    ban: 'Ban member',
    unban: 'Lift ban',
    reason: 'Reason (optional)',
    removeTitle: 'Remove this post?',
    removeBlurb: 'It stops showing in every feed. You can restore it later if needed.',
    banTitle: 'Ban this member?',
    banBlurb: 'Their posts stop showing in the feed. You can lift the ban later. Banning',
  },
  reports: {
    title: 'Reports',
    sub: 'How the club is doing.',
    activeShare: 'Active members (30 days)',
    dormant: 'Dormant members',
    topProducers: 'Top new producers',
    demo: 'Demo Center activity',
    tax: '1099 tracker',
    dormantEmpty: 'No dormant members. Nice.',
    producersEmpty: 'No new producers in the last 90 days yet.',
  },
  game: {
    title: 'Game',
    sub: 'The club game: this month\'s challenge, the Crew team goal, and paddles to review.',
    challengeTitle: 'This month\'s challenge',
    noChallenge: 'No challenge set for this month yet. Create one below.',
    teamGoalTitle: 'Crew team goal',
    noTeamGoal: 'No Crew team goal set for this month yet.',
    finishers: 'members finished',
    flaggedTitle: 'Paddles to review',
    flaggedSub: 'These looked faster than a paddle usually goes, so they earn nothing until you look. Approve the real ones; deny the rest. Nothing is ever taken from the member.',
    flaggedEmpty: 'Nothing to review. Nice.',
    approve: 'Approve',
    deny: 'Deny',
    reviewRequested: 'Member asked for review',
    createTitle: 'Create a challenge',
    createSub: 'One simple goal everyone works. Add a Crew team goal to show on Home.',
    period: 'Month (YYYY-MM)',
    cTitle: 'Challenge title',
    metric: 'Measure',
    goal: 'Goal per member',
    prize: 'Prize line (optional)',
    teamOn: 'Add a Crew team goal',
    teamTitle: 'Team goal title',
    teamGoalValue: 'Team goal total',
    create: 'Create challenge',
    created: 'Challenge created.',
  },
  access: {
    title: 'Access and household',
    grant: 'Grant owner access',
    revoke: 'Revoke owner access',
    grantPrompt: 'Why are you granting owner access? (recorded in the audit log)',
    revokePrompt: 'Why are you revoking owner access? This ends the club for them and their household, and voids pending commission. (recorded in the audit log)',
  },
  common: {
    signOut: 'Sign out',
    retry: 'Try again',
    loadError: 'Could not load that. Check the API connection.',
    cancel: 'Cancel',
    save: 'Save',
    back: 'Back',
    note: 'Note (optional)',
  },
};

// Human labels for the active programs (internal; plain). The legacy "Customer Referral"
// program is retired (ADMIN_3) and intentionally absent. A raw program key falls through to
// programLabel's default so an unexpected value still renders readably.
export const PROGRAM_LABEL: Record<string, string> = {
  brand_ambassador: 'Brand Ambassador',
  demo_center: 'Demo Center',
  pro_content_creator: 'Pro Content Creator',
  inflatable_boarder: 'Inflatable Boarder',
};

export function programLabel(p: string): string {
  return PROGRAM_LABEL[p] ?? p;
}
